import { format, parseISO } from 'date-fns';
import { SurveyLaunch, SurveyLaunchStats, SurveyQuestion, SurveyQuestionSlot } from '../../types/survey';

export const SURVEY_RATING_MIN = 1;
export const SURVEY_RATING_MAX = 10;
export const SURVEY_TEXT_SLOTS = [1, 2, 3, 4] as const;
export const SURVEY_QUESTION_TEXT_MAX_LENGTH = 300;
/** D-A10: below this number of ratings, the average of a round is shown as a small sample. */
const SURVEY_TREND_MIN_RATINGS = 5;

const DISPLAY_DATE_FORMAT = 'dd/MM/yyyy';

export type SurveyTextAccessor = 'text1' | 'text2' | 'text3' | 'text4';

export function isQuestionEnabled(question: SurveyQuestion): boolean {
  return question.enabled !== false;
}

export function roundLabel(number: number): string {
  return `Ronda ${number}`;
}

export function textAccessor(slot: SurveyQuestionSlot): SurveyTextAccessor {
  return `text${slot}` as SurveyTextAccessor;
}

/** Spanish text first, then the first non-blank text, then the fallback. */
export function getQuestionLabel(question: SurveyQuestion | undefined, fallback: string): string {
  if (!question) return fallback;
  const spanishText = question.texts.es?.trim();
  if (spanishText) return spanishText;
  return (
    Object.values(question.texts)
      .find((text) => text.trim() !== '')
      ?.trim() ?? fallback
  );
}

export type QuestionLanguageText = { locale: string; text: string };

/** D-A15: the question in the languages other than Spanish ("EN", text), to show next to the Spanish text. */
export function getOtherLanguageTexts(question: SurveyQuestion | undefined): QuestionLanguageText[] {
  if (!question) return [];
  return Object.entries(question.texts)
    .filter(([locale, text]) => locale !== 'es' && text.trim() !== '')
    .map(([locale, text]) => ({ locale: locale.toUpperCase(), text: text.trim() }));
}

/** D-A15: "EN", or "-" when the language of an answer is unknown. */
export function localeLabel(locale: string | null | undefined): string {
  return locale ? locale.toUpperCase() : '-';
}

export function getEnabledTextQuestions(questions: SurveyQuestion[]): SurveyQuestion[] {
  return questions.filter((question) => question.type === 'text' && isQuestionEnabled(question));
}

export function getRatingQuestion(questions: SurveyQuestion[]): SurveyQuestion | undefined {
  return questions.find((question) => question.type === 'rating' && isQuestionEnabled(question));
}

const emptyTexts = (texts: Record<string, string>): Record<string, string> =>
  Object.fromEntries(Object.keys(texts).map((locale) => [locale, '']));

/** Enables the first disabled text slot. Enabled text slots always form a prefix (see `removeTextQuestion`). */
export function addTextQuestion(questions: SurveyQuestion[]): SurveyQuestion[] {
  const next = questions.find((question) => question.type === 'text' && !isQuestionEnabled(question));
  if (!next) return questions;
  return questions.map((question) =>
    question.slot === next.slot ? { ...question, enabled: true, required: false, texts: emptyTexts(question.texts) } : question,
  );
}

/**
 * Removes a text question and moves the next text questions up one slot, so the enabled text slots stay 1..n and
 * the numbers the employee sees ("Pregunta 2") match the slots.
 */
export function removeTextQuestion(questions: SurveyQuestion[], slot: SurveyQuestionSlot): SurveyQuestion[] {
  const enabledTexts = getEnabledTextQuestions(questions);
  const remaining = enabledTexts.filter((question) => question.slot !== slot);
  if (remaining.length === enabledTexts.length) return questions;

  return questions.map((question) => {
    if (question.type !== 'text') return question;
    const source = remaining[question.slot - 1];
    if (source) return { ...question, enabled: true, required: source.required, texts: { ...source.texts } };
    return { ...question, enabled: false, required: false, texts: emptyTexts(question.texts) };
  });
}

export function setRatingEnabled(questions: SurveyQuestion[], enabled: boolean): SurveyQuestion[] {
  return questions.map((question) => (question.type === 'rating' ? { ...question, enabled } : question));
}

function localesSuffix(missing: string[], locales: string[]): string {
  return locales.length > 1 ? ` (${missing.map((locale) => locale.toUpperCase()).join(', ')})` : '';
}

/** Problems that stop a round from starting, in the order the employee sees them. Empty when the survey is ready. */
export function getSurveyProblems(questions: SurveyQuestion[], locales: string[]): string[] {
  const enabled = questions.filter(isQuestionEnabled);
  if (enabled.length === 0) return ['Añade al menos una pregunta.'];

  const problems: string[] = [];
  const addProblems = (question: SurveyQuestion, name: string): void => {
    const missing = locales.filter((locale) => !question.texts[locale]?.trim());
    if (missing.length > 0) problems.push(`Escribe el texto de ${name}${localesSuffix(missing, locales)}.`);
    // A translation can be longer than the field allows; the backend refuses texts over the limit (after trimming).
    const tooLong = locales.filter((locale) => (question.texts[locale]?.trim().length ?? 0) > SURVEY_QUESTION_TEXT_MAX_LENGTH);
    if (tooLong.length > 0) {
      problems.push(
        `El texto de ${name}${localesSuffix(tooLong, locales)} supera ${SURVEY_QUESTION_TEXT_MAX_LENGTH} caracteres.`,
      );
    }
  };
  getEnabledTextQuestions(questions).forEach((question, index) => addProblems(question, `la pregunta ${index + 1}`));
  const rating = getRatingQuestion(questions);
  if (rating) addProblems(rating, 'la valoración');
  return problems;
}

/** "7,4" — one decimal, Spanish format. */
export function formatAverage(average: number | null | undefined): string {
  if (typeof average !== 'number') return '-';
  return average.toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

export function formatDate(isoDate: string | null): string {
  return isoDate ? format(parseISO(isoDate), DISPLAY_DATE_FORMAT) : '-';
}

export function formatRoundPeriod(launch: Pick<SurveyLaunch, 'startedAt' | 'stoppedAt'>): string {
  const start = formatDate(launch.startedAt);
  return launch.stoppedAt ? `${start} – ${formatDate(launch.stoppedAt)}` : `Desde el ${start}`;
}

/** Share of each rating value, 0 to 100, rounded. */
export function toPercentages(distribution: number[]): number[] {
  const total = distribution.reduce((sum, count) => sum + count, 0);
  return distribution.map((count) => (total === 0 ? 0 : Math.round((count / total) * 100)));
}

export type RatingTone = 'low' | 'mid' | 'high';

/** 1-4 low, 5-7 mid, 8-10 high. Used for colours only. */
export function ratingTone(rating: number): RatingTone {
  if (rating < 5) return 'low';
  if (rating < 8) return 'mid';
  return 'high';
}

export type SurveyRoundTrendRow = Pick<SurveyLaunch, 'id' | 'number' | 'status' | 'startedAt' | 'stoppedAt'> & {
  ratingCount: number;
  average: number | null;
  /** Shown average minus the shown average of the previous round that has one. */
  delta: number | null;
  lowSample: boolean;
  /** D-A15: the questions (any of them, in any language) differ from the previous round in the list. */
  surveyChanged: boolean;
};

/** The enabled questions of a round, in a form that compares equal when the survey is the same. */
const surveySignature = (questions: SurveyQuestion[]): string =>
  JSON.stringify(
    questions
      .filter(isQuestionEnabled)
      .sort((a, b) => a.slot - b.slot)
      .map((question) => [
        question.slot,
        question.type,
        Object.entries(question.texts)
          .map(([locale, text]) => [locale, text.trim()])
          .sort(),
      ]),
  );

const roundToShownAverage = (average: number): number => Math.round(average * 10) / 10;

/**
 * D-A10: one row per round with a rating question, newest first. Only the rating is compared across rounds:
 * text questions of different rounds may be different questions.
 */
export function buildRoundTrend(launches: SurveyLaunch[], stats: SurveyLaunchStats[]): SurveyRoundTrendRow[] {
  const statsById = new Map(stats.map((item) => [item.id, item]));
  const rows: SurveyRoundTrendRow[] = [];
  let previousAverage: number | null = null;
  let previousSignature: string | undefined;

  [...launches]
    .sort((a, b) => a.number - b.number)
    .forEach((launch) => {
      const rating = getRatingQuestion(launch.questions);
      if (!rating) return;

      const launchStats = statsById.get(launch.id);
      const average = typeof launchStats?.average === 'number' ? roundToShownAverage(launchStats.average) : null;
      const ratingCount = launchStats?.ratingCount ?? 0;
      const signature = surveySignature(launch.questions);

      rows.push({
        id: launch.id,
        number: launch.number,
        status: launch.status,
        startedAt: launch.startedAt,
        stoppedAt: launch.stoppedAt,
        ratingCount,
        average: launchStats?.average ?? null,
        delta: average !== null && previousAverage !== null ? roundToShownAverage(average - previousAverage) : null,
        lowSample: ratingCount < SURVEY_TREND_MIN_RATINGS,
        surveyChanged: previousSignature !== undefined && signature !== previousSignature,
      });

      if (average !== null) previousAverage = average;
      previousSignature = signature;
    });

  return rows.reverse();
}

/** "+0,8", "−0,3", "0,0" — signed, one decimal, Spanish format. */
export function formatAverageDelta(delta: number | null): string {
  if (delta === null) return '-';
  const value = formatAverage(Math.abs(delta));
  if (delta > 0) return `+${value}`;
  if (delta < 0) return `−${value}`;
  return value;
}
